import localforage from 'localforage'
import { create } from 'zustand'
import { fetchUserPreferences, saveUserPreference } from '@api/preferences'
import { type GameFavoritesState } from '@app-types/stores/gameFavorites'

const gameFavoritesPreferenceKey = 'gameFavorites'

function gameFavoritesStorageKey(userId: number) {
  return `spa:games:${userId}:favorites`
}

function normalizeFavoriteAppIds(value: unknown) {
  if (!Array.isArray(value)) {
    return []
  }

  return value.filter((item): item is number => Number.isInteger(item))
}

function saveFavorites(csrfToken: string | null, favoriteAppIds: number[]) {
  if (csrfToken === null) {
    return
  }

  void saveUserPreference(
    gameFavoritesPreferenceKey,
    favoriteAppIds,
    csrfToken,
  )
}

async function loadStoredFavorites(userId: number) {
  return normalizeFavoriteAppIds(
    await localforage.getItem(gameFavoritesStorageKey(userId)),
  )
}

export const useGameFavoritesStore = create<GameFavoritesState>((set, get) => ({
  favoriteAppIds: [],
  isLoaded: false,
  userId: null,
  csrfToken: null,

  loadFavorites: async (userId, csrfToken) => {
    const state = get()

    if (state.isLoaded && state.userId === userId) {
      return
    }

    try {
      const preferences = await fetchUserPreferences()
      const preference = preferences[gameFavoritesPreferenceKey]
      const favoriteAppIds =
        preference === undefined
          ? await loadStoredFavorites(userId)
          : normalizeFavoriteAppIds(preference)

      if (preference === undefined && favoriteAppIds.length > 0) {
        saveFavorites(csrfToken, favoriteAppIds)
      }

      set({
        csrfToken,
        favoriteAppIds,
        isLoaded: true,
        userId,
      })
    } catch {
      set({
        csrfToken,
        favoriteAppIds: await loadStoredFavorites(userId),
        isLoaded: true,
        userId,
      })
    }
  },

  reorderFavorite: (sourceAppId, targetAppId) => {
    if (sourceAppId === targetAppId) {
      return
    }

    set((state) => {
      const sourceIndex = state.favoriteAppIds.indexOf(sourceAppId)
      const targetIndex = state.favoriteAppIds.indexOf(targetAppId)

      if (sourceIndex < 0 || targetIndex < 0) {
        return state
      }

      const favoriteAppIds = [...state.favoriteAppIds]
      const [source] = favoriteAppIds.splice(sourceIndex, 1)
      favoriteAppIds.splice(targetIndex, 0, source)
      saveFavorites(state.csrfToken, favoriteAppIds)

      return { favoriteAppIds }
    })
  },

  resetFavorites: () => {
    set({
      csrfToken: null,
      favoriteAppIds: [],
      isLoaded: false,
      userId: null,
    })
  },

  toggleFavorite: (appId) => {
    set((state) => {
      const favoriteAppIds = state.favoriteAppIds.includes(appId)
        ? state.favoriteAppIds.filter((item) => item !== appId)
        : [...state.favoriteAppIds, appId]

      saveFavorites(state.csrfToken, favoriteAppIds)

      return { favoriteAppIds }
    })
  },
}))
